import { useCallback, useRef, useState } from 'react';

import { useModal } from 'mui-modal-provider';

import { CAST_AI } from '@cast/constants';
import { Cluster } from '@cast/types';

import { SlideDialog } from 'hooks/dialogs/cluster/_components/SlideDialog';

import { SecurityFeaturesSetupSlide } from './_components/SecurityFeaturesSetupSlide';
import { SecurityFeaturesSuccessSlide } from './_components/SecurityFeaturesSuccessSlide';
import { EnableSecurityFeaturesSlide, SecurityFeatureSelection } from './types';
import { EnablingFeaturesStep, ErrorStep } from '../_components/new-onboarding';
import { useWasSecurityEnabled } from '../_hooks/useWasSecurityEnabled';

import {
  useCluster,
  ClusterProvider,
} from '@cast/shared-cluster-context-data-access';

import { ShowModalOutput } from '@cast/design-system';

const EnableSecurityFeaturesDialogInner = ({
  destroy,
}: {
  destroy: () => void;
}) => {
  const { cluster } = useCluster();
  const [activeSlide, setActiveSlide] =
    useState<EnableSecurityFeaturesSlide>('setup');
  const [featureSelection, setFeatureSelection] =
    useState<SecurityFeatureSelection>({
      vulnerabilityDetection: true,
      compliance: true,
    });

  const updateFeature = useCallback(
    (feature: keyof SecurityFeatureSelection, checked: boolean) => {
      setFeatureSelection((prev) => ({ ...prev, [feature]: checked }));
    },
    []
  );

  useWasSecurityEnabled({
    clusterId: cluster.id,
    onSuccess: () => setActiveSlide('success'),
    onError: () => setActiveSlide('error'),
    enabled: activeSlide === 'inProgress',
  });

  return (
    <SlideDialog
      open
      testId="enable-security-features-dialog"
      onClose={destroy}
      wideSlides={['setup']}
      activeSlide={activeSlide}
      slides={{
        setup: (
          <SecurityFeaturesSetupSlide
            featureSelection={featureSelection}
            updateFeature={updateFeature}
            onNext={() => setActiveSlide('inProgress')}
            onClose={destroy}
          />
        ),
        inProgress: (
          <EnablingFeaturesStep
            onClose={destroy}
            onBack={() => setActiveSlide('setup')}
          />
        ),
        error: (
          <ErrorStep
            title={`Failed to enable ${CAST_AI} security features`}
            testId="enabling-features-failed"
            onRetry={() => setActiveSlide('setup')}
            onClose={destroy}
          />
        ),
        success: (
          <SecurityFeaturesSuccessSlide
            featureSelection={featureSelection}
            onClose={destroy}
          />
        ),
      }}
    />
  );
};

type Props = {
  cluster: Cluster;
};

type FullProps = {
  destroy: () => void;
} & Props;

const EnableSecurityFeaturesDialog = ({ destroy, cluster }: FullProps) => {
  return (
    <ClusterProvider cluster={cluster}>
      <EnableSecurityFeaturesDialogInner destroy={destroy} />
    </ClusterProvider>
  );
};

export const useEnableSecurityFeaturesDialog = ({
  cluster,
}: {
  cluster: Cluster;
}) => {
  const modalRef = useRef<ShowModalOutput>(null);
  const { showModal } = useModal({ disableAutoDestroy: true });

  const openEnableSecurityFeaturesDialog = () => {
    modalRef.current = showModal(EnableSecurityFeaturesDialog, {
      destroy: () => modalRef.current?.destroy(),
      cluster,
    });
  };

  return { openEnableSecurityFeaturesDialog };
};
